import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { useAuth, API, authFetch } from "@/App";
import { toast } from "sonner";

import { 
  ArrowLeft, Target, TrendingUp, DollarSign, Plus, X, Save, Loader2,
  PiggyBank, BarChart3, Calendar
} from "lucide-react";

export const VisionGoalsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [summary, setSummary] = useState(null);
  
  const [vision, setVision] = useState({
    vision_statement: "",
    one_year_goals: [],
    income_streams: [],
    total_annual_revenue_goal: 0,
    total_annual_profit_goal: 0,
    monthly_targets: {},
    notes: ""
  });

  const [newGoal, setNewGoal] = useState("");
  const [newStream, setNewStream] = useState({
    name: "",
    description: "",
    monthly_goal: 0,
    annual_goal: 0,
    current_monthly: 0,
    profit_margin: 0
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [visionRes, summaryRes] = await Promise.all([
        authFetch(`${API}/vision-goals`, {  }),
        authFetch(`${API}/vision-goals/summary`, {  })
      ]);

      if (visionRes.ok) {
        const data = await visionRes.json();
        if (data) {
          setVision({
            vision_statement: data.vision_statement || "",
            one_year_goals: data.one_year_goals || [],
            income_streams: data.income_streams || [],
            total_annual_revenue_goal: data.total_annual_revenue_goal || 0,
            total_annual_profit_goal: data.total_annual_profit_goal || 0,
            monthly_targets: data.monthly_targets || {},
            notes: data.notes || ""
          });
        }
      }

      if (summaryRes.ok) {
        setSummary(await summaryRes.json());
      }
    } catch (_error) {
      console.error("Error fetching data:", _error);
    } finally {
      setLoading(false);
    }
  };

  const addGoal = () => {
    if (newGoal.trim()) {
      setVision({ ...vision, one_year_goals: [...vision.one_year_goals, newGoal.trim()] });
      setNewGoal("");
    }
  };

  const removeGoal = (index) => {
    setVision({ ...vision, one_year_goals: vision.one_year_goals.filter((_, i) => i !== index) });
  };

  const addIncomeStream = () => {
    if (newStream.name.trim()) {
      const stream = {
        ...newStream,
        annual_goal: newStream.monthly_goal * 12
      };
      setVision({ ...vision, income_streams: [...vision.income_streams, stream] });
      setNewStream({
        name: "",
        description: "",
        monthly_goal: 0,
        annual_goal: 0,
        current_monthly: 0,
        profit_margin: 0
      });
    }
  };

  const removeIncomeStream = (index) => {
    setVision({ ...vision, income_streams: vision.income_streams.filter((_, i) => i !== index) });
  };

  const updateIncomeStream = (index, field, value) => {
    const streams = [...vision.income_streams];
    streams[index] = { ...streams[index], [field]: value };
    if (field === "monthly_goal") {
      streams[index].annual_goal = value * 12;
    }
    setVision({ ...vision, income_streams: streams });
  };

  const calculateTotals = () => {
    const totalMonthly = vision.income_streams.reduce((sum, s) => sum + (s.monthly_goal || 0), 0);
    const totalAnnual = vision.income_streams.reduce((sum, s) => sum + (s.annual_goal || 0), 0);
    const totalCurrent = vision.income_streams.reduce((sum, s) => sum + (s.current_monthly || 0), 0);
    const avgMargin = vision.income_streams.length > 0 
      ? vision.income_streams.reduce((sum, s) => sum + (s.profit_margin || 0), 0) / vision.income_streams.length 
      : 0;
    
    return { totalMonthly, totalAnnual, totalCurrent, avgMargin };
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const totals = calculateTotals();
      const dataToSave = {
        ...vision,
        total_annual_revenue_goal: totals.totalAnnual,
        total_annual_profit_goal: totals.totalAnnual * (totals.avgMargin / 100)
      };

      const response = await authFetch(`${API}/vision-goals`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        
        body: JSON.stringify(dataToSave)
      });

      if (response.ok) {
        toast.success("Vision & goals saved!");
        fetchData();
      } else {
        toast.error("Failed to save");
      }
    } catch (_error) {
      toast.error("An error occurred");
    } finally {
      setSaving(false);
    }
  };

  const totals = calculateTotals();
  const progressPercent = totals.totalMonthly > 0 ? (totals.totalCurrent / totals.totalMonthly) * 100 : 0;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <div className="w-8 h-8 border-2 border-emerald-900 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="vision-goals-page">
      {/* Header */}
      <div className="bg-white border-b border-neutral-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate("/dashboard")}
                className="text-neutral-600"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Dashboard
              </Button>
              <div>
                <h1 className="text-xl font-semibold text-neutral-900">1-Year Vision & Financial Goals</h1>
                <p className="text-sm text-neutral-500">Plan your path to success</p>
              </div>
            </div>
            <Button 
              onClick={handleSave}
              disabled={saving}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="save-vision-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8 space-y-6">
        {/* Explanation Card */}
        <Card className="border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50">
          <CardContent className="p-5">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
                <Target className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <h3 className="font-semibold text-neutral-900 mb-1">Vision & Financial Goals Planner</h3>
                <p className="text-sm text-neutral-600 mb-3">
                  Map out your business vision and set clear financial targets. Define your income streams, 
                  set annual revenue and profit goals, and break them down into actionable monthly targets. 
                  Track your progress and stay accountable to your growth plan.
                </p>
                <div className="flex flex-wrap gap-2 text-xs">
                  <span className="px-2 py-1 bg-white rounded border border-emerald-200 text-emerald-700">Vision Statement</span>
                  <span className="px-2 py-1 bg-white rounded border border-emerald-200 text-emerald-700">Income Streams</span>
                  <span className="px-2 py-1 bg-white rounded border border-emerald-200 text-emerald-700">Revenue Goals</span>
                  <span className="px-2 py-1 bg-white rounded border border-emerald-200 text-emerald-700">Progress Tracking</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="border-neutral-200">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <DollarSign className="w-5 h-5 text-emerald-500" />
              </div>
              <p className="text-2xl font-semibold text-neutral-900">${totals.totalMonthly.toLocaleString()}</p>
              <p className="text-sm text-neutral-500">Monthly Goal</p>
            </CardContent>
          </Card>
          
          <Card className="border-neutral-200">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <TrendingUp className="w-5 h-5 text-blue-500" />
              </div>
              <p className="text-2xl font-semibold text-neutral-900">${totals.totalAnnual.toLocaleString()}</p>
              <p className="text-sm text-neutral-500">Annual Goal</p>
            </CardContent>
          </Card>

          <Card className="border-neutral-200">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <BarChart3 className="w-5 h-5 text-amber-500" />
              </div>
              <p className="text-2xl font-semibold text-neutral-900">${totals.totalCurrent.toLocaleString()}</p>
              <p className="text-sm text-neutral-500">Current Monthly</p>
            </CardContent>
          </Card>

          <Card className="border-neutral-200">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <PiggyBank className="w-5 h-5 text-purple-500" />
              </div>
              <p className="text-2xl font-semibold text-neutral-900">{totals.avgMargin.toFixed(0)}%</p>
              <p className="text-sm text-neutral-500">Avg Profit Margin</p>
            </CardContent>
          </Card>
        </div>

        {/* Progress Bar */}
        <Card className="border-neutral-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-3">
              <span className="font-medium text-neutral-700">Monthly Revenue Progress</span>
              <span className="font-mono text-emerald-600">{progressPercent.toFixed(0)}%</span>
            </div>
            <Progress value={Math.min(progressPercent, 100)} className="h-3" />
            <p className="text-sm text-neutral-500 mt-2">
              ${totals.totalCurrent.toLocaleString()} of ${totals.totalMonthly.toLocaleString()} monthly goal
            </p>
          </CardContent>
        </Card>

        {/* Vision Statement */}
        <Card className="border-neutral-200">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg font-semibold flex items-center">
              <Target className="w-5 h-5 mr-2 text-emerald-600" />
              1-Year Vision Statement
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              value={vision.vision_statement}
              onChange={(e) => setVision({ ...vision, vision_statement: e.target.value })}
              placeholder="Describe where you want your business to be in one year. Be specific about revenue, clients, impact, and lifestyle..."
              className="min-h-32"
              data-testid="vision-statement-textarea"
            />
          </CardContent>
        </Card>

        {/* 1-Year Goals */}
        <Card className="border-neutral-200">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg font-semibold flex items-center">
              <Calendar className="w-5 h-5 mr-2 text-blue-500" />
              Key Goals for This Year
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 mb-4">
              {vision.one_year_goals.map((goal, index) => (
                <div key={index} className="flex items-center justify-between bg-blue-50 px-4 py-3 rounded-lg">
                  <span className="text-neutral-700">{goal}</span>
                  <button onClick={() => removeGoal(index)} className="text-neutral-400 hover:text-red-500">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
            <div className="flex space-x-2">
              <Input
                value={newGoal}
                onChange={(e) => setNewGoal(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && addGoal()}
                placeholder="Add a goal for this year..."
                data-testid="new-goal-input"
              />
              <Button onClick={addGoal} className="bg-blue-500 hover:bg-blue-600">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Income Streams */}
        <Card className="border-neutral-200">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg font-semibold flex items-center">
              <DollarSign className="w-5 h-5 mr-2 text-emerald-600" />
              Income Streams
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Existing Streams */}
            <div className="space-y-4 mb-6">
              {vision.income_streams.map((stream, index) => (
                <div key={index} className="p-4 bg-neutral-50 rounded-lg border border-neutral-200">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h4 className="font-semibold text-neutral-900">{stream.name}</h4>
                      {stream.description && <p className="text-sm text-neutral-500">{stream.description}</p>}
                    </div>
                    <button onClick={() => removeIncomeStream(index)} className="text-neutral-400 hover:text-red-500">
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <Label className="text-xs text-neutral-500">Monthly Goal</Label>
                      <Input
                        type="number"
                        value={stream.monthly_goal}
                        onChange={(e) => updateIncomeStream(index, "monthly_goal", parseFloat(e.target.value) || 0)}
                        className="h-9"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-neutral-500">Annual Goal</Label>
                      <Input
                        type="number"
                        value={stream.annual_goal}
                        disabled
                        className="h-9 bg-neutral-100"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-neutral-500">Current Monthly</Label>
                      <Input
                        type="number"
                        value={stream.current_monthly}
                        onChange={(e) => updateIncomeStream(index, "current_monthly", parseFloat(e.target.value) || 0)}
                        className="h-9"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-neutral-500">Profit Margin %</Label>
                      <Input
                        type="number"
                        value={stream.profit_margin}
                        onChange={(e) => updateIncomeStream(index, "profit_margin", parseFloat(e.target.value) || 0)}
                        className="h-9"
                      />
                    </div>
                  </div>
                  <div className="mt-3">
                    <Progress 
                      value={stream.monthly_goal > 0 ? (stream.current_monthly / stream.monthly_goal) * 100 : 0} 
                      className="h-2" 
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* Add New Stream */}
            <div className="p-4 border-2 border-dashed border-neutral-200 rounded-lg">
              <h4 className="font-medium text-neutral-700 mb-4">Add New Income Stream</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <Label>Stream Name</Label>
                  <Input
                    value={newStream.name}
                    onChange={(e) => setNewStream({ ...newStream, name: e.target.value })}
                    placeholder="e.g., Consulting, Courses, Products"
                    data-testid="new-stream-name-input"
                  />
                </div>
                <div>
                  <Label>Description (optional)</Label>
                  <Input
                    value={newStream.description}
                    onChange={(e) => setNewStream({ ...newStream, description: e.target.value })}
                    placeholder="Brief description"
                  />
                </div>
                <div>
                  <Label>Monthly Goal ($)</Label>
                  <Input
                    type="number"
                    value={newStream.monthly_goal || ""}
                    onChange={(e) => setNewStream({ ...newStream, monthly_goal: parseFloat(e.target.value) || 0 })}
                    placeholder="0"
                    data-testid="new-stream-monthly-goal-input"
                  />
                </div>
                <div>
                  <Label>Current Monthly ($)</Label>
                  <Input
                    type="number"
                    value={newStream.current_monthly || ""}
                    onChange={(e) => setNewStream({ ...newStream, current_monthly: parseFloat(e.target.value) || 0 })}
                    placeholder="0"
                  />
                </div>
                <div>
                  <Label>Profit Margin (%)</Label>
                  <Input
                    type="number"
                    value={newStream.profit_margin || ""}
                    onChange={(e) => setNewStream({ ...newStream, profit_margin: parseFloat(e.target.value) || 0 })}
                    placeholder="0"
                  />
                </div>
              </div>
              <Button 
                onClick={addIncomeStream}
                disabled={!newStream.name.trim()}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="add-stream-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Income Stream
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Additional Notes */}
        <Card className="border-neutral-200">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg font-semibold">Additional Notes</CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              value={vision.notes}
              onChange={(e) => setVision({ ...vision, notes: e.target.value })}
              placeholder="Any additional thoughts, strategies, or milestones for your 1-year plan..."
              className="min-h-24"
              data-testid="vision-notes-textarea"
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default VisionGoalsPage;
